// src/components/meetings/MeetingDetails.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Calendar, Clock, MapPin, User, Edit, Trash2,
  ArrowLeft, FileText, Users, CheckCircle, AlertCircle,
  Building, Briefcase, Target, Flag
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { AppContext } from '../../context/AppContext';
import { db } from '../../firebase/config';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';

const MeetingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  
  const [meeting, setMeeting] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  
  // Fetch meeting data
  useEffect(() => {
    const fetchMeeting = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const meetingDoc = await getDoc(doc(db, "meetings", id));
        
        if (meetingDoc.exists()) {
          const data = meetingDoc.data();
          setMeeting({
            id: meetingDoc.id,
            ...data,
            date: data.date?.toDate() || null,
            endTime: data.endTime?.toDate() || null,
            createdAt: data.createdAt?.toDate() || null,
            updatedAt: data.updatedAt?.toDate() || null
          });
        } else {
          setError("Meeting not found");
        }
      } catch (err) {
        console.error("Error fetching meeting:", err);
        setError("Failed to load meeting details");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMeeting();
  }, [id]);
  
  const handleDelete = async () => {
    try {
      setIsLoading(true);
      await deleteDoc(doc(db, "meetings", id));
      navigate('/meetings');
    } catch (err) {
      console.error("Error deleting meeting:", err);
      setError("Failed to delete meeting");
      setIsLoading(false);
    }
  };
  
  // Format dates
  const formatDateTime = (date) => {
    if (!date) return 'Not specified';
    return format(date, 'MMM d, yyyy • h:mm a');
  };
  
  const formatDuration = () => {
    if (!meeting?.date || !meeting?.endTime) return 'Duration not specified';
    
    const start = new Date(meeting.date);
    const end = new Date(meeting.endTime);
    const durationMs = end - start;
    const minutes = Math.floor(durationMs / (1000 * 60));
    
    if (minutes < 60) {
      return `${minutes} minutes`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours} hour${hours > 1 ? 's' : ''}${remainingMinutes > 0 ? ` ${remainingMinutes} minutes` : ''}`;
    }
  };
  
  const getMeetingStatus = () => {
    if (!meeting?.date) return { label: 'Not Scheduled', color: 'bg-gray-100 text-gray-800' };
    
    const now = new Date();
    const meetingDate = new Date(meeting.date);
    
    if (meetingDate < now) {
      return { label: 'Completed', color: 'bg-gray-100 text-gray-800' };
    }
    
    // Check if meeting is happening today
    const today = new Date();
    const isToday = meetingDate.getDate() === today.getDate() &&
                    meetingDate.getMonth() === today.getMonth() &&
                    meetingDate.getFullYear() === today.getFullYear();
                    
    if (isToday) {
      return { label: 'Today', color: 'bg-green-100 text-green-800' };
    }
    
    // Calculate days until meeting
    const diffTime = Math.abs(meetingDate - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 3) {
      return { label: 'Upcoming', color: 'bg-yellow-100 text-yellow-800' };
    }
    
    return { label: 'Scheduled', color: 'bg-blue-100 text-blue-800' };
  };

  // Get priority badge details
  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'urgent':
        return { label: 'Urgent', color: 'bg-purple-100 text-purple-800', icon: <Flag className="h-4 w-4" /> };
      case 'high':
        return { label: 'High Priority', color: 'bg-red-100 text-red-800', icon: <Flag className="h-4 w-4" /> };
      case 'medium':
        return { label: 'Medium Priority', color: 'bg-yellow-100 text-yellow-800', icon: <Flag className="h-4 w-4" /> };
      case 'low':
        return { label: 'Low Priority', color: 'bg-green-100 text-green-800', icon: <Flag className="h-4 w-4" /> };
      default:
        return null;
    }
  };
  
  // Check if current user can edit/delete the meeting
  const canEditMeeting = () => {
    if (!meeting || !userProfile) return false;
    
    // Allow editing if user created the meeting or if they're from the same business
    return meeting.createdBy === userProfile.id || 
           meeting.businessId === activeBusiness?.id;
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#190192]"></div>
        <p className="ml-2">Loading meeting details...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-600">{error}</p>
              <button
                onClick={() => navigate('/meetings')}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Return to meetings
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Meeting not found</h3>
          <p className="mt-1 text-sm text-gray-500">The meeting you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/meetings')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#190192] hover:bg-blue-700"
          >
            Return to Meetings
          </button>
        </div>
      </div>
    );
  }

  const statusDetails = getMeetingStatus();
  const priorityBadge = getPriorityBadge(meeting.priority);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <button
                onClick={() => navigate('/meetings')}
                className="mt-1 text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-2xl font-bold text-gray-900">{meeting.title}</h1>
                  
                  {/* Status Badge */}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusDetails.color}`}>
                    {statusDetails.label}
                  </span>

                  {/* Priority Badge */}
                  {priorityBadge && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityBadge.color}`}>
                      {priorityBadge.icon}
                      <span className="ml-1">{priorityBadge.label}</span>
                    </span>
                  )}
                </div>

                {/* Meeting Type */}
                {meeting.type && (
                  <div className="mb-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      <Briefcase className="h-4 w-4 mr-1" />
                      {meeting.type}
                    </span>
                  </div>
                )}

                {/* Business Info */}
                {activeBusiness && (
                  <p className="text-sm text-gray-500 flex items-center">
                    <Building className="h-4 w-4 mr-1" />
                    {activeBusiness.name}
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {canEditMeeting() && (
              <div className="flex items-center space-x-3">
                <Link
                  to={`/meetings/edit/${meeting.id}`}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Link>
                
                <button
                  onClick={() => setShowDeleteConfirmation(true)}
                  className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Meeting Details */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Meeting Information</h2>
        </div>
        
        <div className="px-6 py-4">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <Calendar className="h-4 w-4 mr-1" /> Date & Time
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                <div>{formatDateTime(meeting.date)}</div>
                <div className="text-gray-500">Duration: {formatDuration()}</div>
              </dd>
            </div>
            
            {meeting.location && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <MapPin className="h-4 w-4 mr-1" /> Location
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {meeting.location}
                </dd>
              </div>
            )}
            
            {meeting.chairperson && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <User className="h-4 w-4 mr-1" /> Meeting Chair
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {meeting.chairperson}
                </dd>
              </div>
            )}

            {meeting.department && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <Building className="h-4 w-4 mr-1" /> Department
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {meeting.department}
                </dd>
              </div>
            )}
          </dl>

          {meeting.description && (
            <div className="mt-6">
              <dt className="text-sm font-medium text-gray-500 mb-2">Description</dt>
              <dd className="text-sm text-gray-900 leading-relaxed">
                {meeting.description}
              </dd>
            </div>
          )}
        </div>
      </div>

      {/* Participants */}
      {meeting.participants && meeting.participants.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Participants ({meeting.participants.length})
            </h3>
          </div>
          
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {meeting.participants.map((participant, index) => (
                <div key={participant.id || index} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {participant.name || 'Unknown Participant'}
                    </p>
                    {participant.department && (
                      <p className="text-xs text-gray-500 truncate">
                        {participant.position} • {participant.department}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Meeting Actions</h3>
        </div>
        
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Link
              to={`/meetings/agenda/${meeting.id}`}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <FileText className="h-4 w-4 mr-2" />
              Manage Agenda
            </Link>
            
            <Link
              to={`/meetings/minutes/${meeting.id}`}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <FileText className="h-4 w-4 mr-2" />
              Capture Minutes
            </Link>
            
            {canEditMeeting() && (
              <Link
                to={`/meetings/edit/${meeting.id}`}
                className="flex items-center justify-center px-4 py-3 border border-transparent rounded-md text-sm font-medium text-white bg-[#190192] hover:bg-blue-700"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Meeting
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Meeting
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this meeting? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {isLoading ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmation(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingDetails;